// Bank Statement store — data layer for the upcoming Bank Statements
// tab inside the Purchases module. Phase 1: data model + CRUD only.
//
// Architecture follows the rest of the app:
//   - Firestore (REST API) is the source of truth
//   - localStorage is a per-key speed cache for instant reads
//   - All writes are deterministic-ID PATCHes (re-uploading the same
//     statement overwrites the same docs instead of duplicating)
//   - All collections are department-scoped (kitchen | bakery | yawehs | tuckshop)
//
// Collections owned by this module (separate from existing P&L pipelines):
//   bankAccounts        — { dept }_{ accountNumber }
//   bankTransactions    — { dept }_{ accountNumber }_{ date }_{ descriptionHash }
//   bankCharges         — { dept }_{ accountNumber }_{ yearMonth }_{ chargeType }
//   expenseCategories   — { dept }_{ categorySlug }
//
// These collections are deliberately decoupled from accounting.js /
// getPeriodPnL() — Phase 4 (later task) will wire bank debits into
// the existing P&L. For now they live as a parallel pipeline.

import * as Sentry from '@sentry/react'
import { restSet, restDelete, restQuery } from './firestoreRest'

// Error handling helpers — `firestoreRest` already captures its own
// REST errors to Sentry (via `captureAndThrow`), but those captures
// lack business context (which bankStore function was active, which
// record, which department). These wrappers add a second capture
// with that context so production traces can point at the culprit.
// Both helpers rethrow so callers can still react (show a toast,
// roll back UI state, etc.) rather than silently continuing.
async function restSetSafe(path, record, context) {
  try {
    await restSet(path, record)
    return record
  } catch (err) {
    console.warn('[bankStore]', context.fn, 'failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', ...context } })
    throw err
  }
}

async function restDeleteSafe(path, context) {
  try {
    await restDelete(path)
    return true
  } catch (err) {
    console.warn('[bankStore]', context.fn, 'failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', ...context } })
    throw err
  }
}

// ─── Hash + slug helpers ──────────────────────────────────────────

/** DJB2 string hash → unsigned 32-bit → base36. Deterministic, sync,
 *  ~10 chars output. Used to keep deterministic doc IDs short and
 *  readable while still avoiding collisions on the description field
 *  of bank transactions. */
export function hashString(input) {
  const s = String(input ?? '')
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) + s.charCodeAt(i) // h * 33 + c
    h |= 0 // force 32-bit
  }
  // Convert to unsigned then base36
  return (h >>> 0).toString(36)
}

/** Lowercase, alpha-numeric-and-hyphen slug. */
export function slugify(input) {
  return String(input ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// ─── Default expense category seed ────────────────────────────────

export const DEFAULT_EXPENSE_CATEGORIES = [
  { name: 'Ingredients / Stock', type: 'expense',     colour: '#10b981', keywords: ['stock', 'ingredient', 'supplier', 'wholesale', 'meat', 'flour', 'oil', 'sugar'] },
  { name: 'Utilities',           type: 'expense',     colour: '#3b82f6', keywords: ['eskom', 'electricity', 'water', 'municipality', 'gas', 'utility'] },
  { name: 'Wages / Salaries',    type: 'expense',     colour: '#a855f7', keywords: ['wage', 'salary', 'salaries', 'staff', 'payroll'] },
  { name: 'Packaging',           type: 'expense',     colour: '#f59e0b', keywords: ['packaging', 'plastic', 'box', 'bag', 'container', 'cup'] },
  { name: 'Transport',           type: 'expense',     colour: '#06b6d4', keywords: ['fuel', 'petrol', 'diesel', 'transport', 'delivery', 'uber'] },
  { name: 'Equipment',           type: 'expense',     colour: '#6366f1', keywords: ['equipment', 'tool', 'machine', 'fridge', 'freezer', 'oven'] },
  { name: 'Rent',                type: 'expense',     colour: '#ec4899', keywords: ['rent', 'rental', 'lease', 'landlord'] },
  { name: 'Bank Charges',        type: 'bank_charge', colour: '#ef4444', keywords: ['fee', 'fees', 'charge', 'service', 'bank', 'fnb'] },
  { name: 'Online Payments',     type: 'expense',     colour: '#14b8a6', keywords: ['yoco', 'paypal', 'payfast', 'snapscan', 'zapper', 'paystack'] },
  { name: 'Marketing',           type: 'expense',     colour: '#eab308', keywords: ['marketing', 'advert', 'facebook', 'instagram', 'google ads', 'flyer'] },
  { name: 'Other',               type: 'expense',     colour: '#9ca3af', keywords: [] },
]

// ─── localStorage cache helpers ───────────────────────────────────

const LS_PREFIX = 'desres_bank_'

function lsKey(collection, dept) { return `${LS_PREFIX}${collection}_${dept}` }

function lsLoad(collection, dept) {
  try { return JSON.parse(localStorage.getItem(lsKey(collection, dept)) || '[]') }
  catch { return [] }
}

function lsSave(collection, dept, list) {
  try { localStorage.setItem(lsKey(collection, dept), JSON.stringify(list)) }
  catch { /* quota / serialization — ignore */ }
}

function upsertLocal(collection, dept, record) {
  const list = lsLoad(collection, dept)
  const idx = list.findIndex(r => r.id === record.id)
  if (idx >= 0) list[idx] = record
  else list.push(record)
  lsSave(collection, dept, list)
}

function removeLocal(collection, dept, id) {
  const list = lsLoad(collection, dept).filter(r => r.id !== id)
  lsSave(collection, dept, list)
}

// ─── Deterministic ID builders ────────────────────────────────────

/** {dept}_{accountNumber} — last 4 digits of the account number. */
export function bankAccountId(dept, accountNumber) {
  return `${dept}_${String(accountNumber).slice(-4)}`.toLowerCase()
}

/** {dept}_{accountNumber}_{date}_{descriptionHash} */
export function bankTransactionId(dept, accountNumber, date, description) {
  const acct = String(accountNumber).slice(-4)
  return `${dept}_${acct}_${date}_${hashString(description)}`.toLowerCase()
}

/** {dept}_{accountNumber}_{yearMonth}_{chargeType} */
export function bankChargeId(dept, accountNumber, yearMonth, chargeType) {
  const acct = String(accountNumber).slice(-4)
  return `${dept}_${acct}_${yearMonth}_${chargeType}`.toLowerCase()
}

/** {dept}_{categorySlug} */
export function expenseCategoryId(dept, name) {
  return `${dept}_${slugify(name)}`.toLowerCase()
}

// ─── bankAccounts CRUD ────────────────────────────────────────────

export function getBankAccounts(dept) {
  return lsLoad('bankAccounts', dept)
}

export async function refreshBankAccounts(dept) {
  try {
    const list = await restQuery('bankAccounts', 'departmentId', dept)
    lsSave('bankAccounts', dept, list)
    return list
  } catch (err) {
    console.warn('[bankStore] refreshBankAccounts failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', fn: 'refreshBankAccounts', dept } })
    return lsLoad('bankAccounts', dept)
  }
}

export async function saveBankAccount({ departmentId, bankName, accountNumber, accountName }) {
  const acct = String(accountNumber || '').slice(-4)
  if (!acct) throw new Error('accountNumber is required')
  const id = bankAccountId(departmentId, acct)
  const existing = getBankAccounts(departmentId).find(a => a.id === id)
  const record = {
    id,
    departmentId,
    bankName: bankName || '',
    accountNumber: acct,
    accountName: accountName || '',
    createdAt: existing?.createdAt || new Date().toISOString(),
  }
  await restSetSafe(`bankAccounts/${id}`, record, { fn: 'saveBankAccount', id, departmentId })
  upsertLocal('bankAccounts', departmentId, record)
  return record
}

export async function deleteBankAccount(dept, id) {
  await restDeleteSafe(`bankAccounts/${id}`, { fn: 'deleteBankAccount', id, dept })
  removeLocal('bankAccounts', dept, id)
  return true
}

// ─── bankTransactions CRUD ────────────────────────────────────────

export function getBankTransactions(dept, { accountNumber, dateFrom, dateTo } = {}) {
  let list = lsLoad('bankTransactions', dept)
  if (accountNumber) {
    const acct = String(accountNumber).slice(-4)
    list = list.filter(t => String(t.accountNumber).slice(-4) === acct)
  }
  if (dateFrom) list = list.filter(t => t.date >= dateFrom)
  if (dateTo) list = list.filter(t => t.date <= dateTo)
  return list
}

export async function refreshBankTransactions(dept) {
  try {
    const list = await restQuery('bankTransactions', 'departmentId', dept)
    lsSave('bankTransactions', dept, list)
    return list
  } catch (err) {
    console.warn('[bankStore] refreshBankTransactions failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', fn: 'refreshBankTransactions', dept } })
    return lsLoad('bankTransactions', dept)
  }
}

/** Save a single bank transaction. ID is deterministic from dept +
 *  account + date + description, so re-uploading the same statement
 *  overwrites instead of duplicating. */
export async function saveBankTransaction(tx) {
  const {
    departmentId, accountNumber, bankName,
    date, description, amount, type,
    category, subcategory, notes,
    source, statementPeriod, confirmed,
  } = tx
  if (!departmentId) throw new Error('departmentId is required')
  if (!accountNumber) throw new Error('accountNumber is required')
  if (!date) throw new Error('date is required')
  if (!description) throw new Error('description is required')
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    throw new Error('amount must be a finite number')
  }

  const id = bankTransactionId(departmentId, accountNumber, date, description)
  const acct = String(accountNumber).slice(-4)
  const record = {
    id,
    departmentId,
    accountNumber: acct,
    bankName: bankName || '',
    date,
    description,
    amount: Math.abs(amount),
    type: type || 'debit',
    category: category || 'Other',
    subcategory: subcategory || '',
    notes: notes || '',
    source: source || 'statement_upload',
    statementPeriod: statementPeriod || '',
    confirmed: !!confirmed,
    createdAt: new Date().toISOString(),
  }
  await restSetSafe(`bankTransactions/${id}`, record, { fn: 'saveBankTransaction', id, departmentId, accountNumber: acct })
  upsertLocal('bankTransactions', departmentId, record)
  return record
}

/** Save many transactions in sequence. Returns { saved, failed }. */
export async function saveBankTransactionsBatch(txs) {
  let saved = 0
  let failed = 0
  for (const tx of txs) {
    try { await saveBankTransaction(tx); saved++ }
    catch (err) {
      console.warn('[bankStore] saveBankTransactionsBatch row failed:', err?.message)
      failed++
    }
  }
  return { saved, failed }
}

export async function updateBankTransaction(id, dept, updates) {
  const list = lsLoad('bankTransactions', dept)
  const existing = list.find(t => t.id === id)
  if (!existing) return null
  const updated = { ...existing, ...updates, id, updatedAt: new Date().toISOString() }
  await restSetSafe(`bankTransactions/${id}`, updated, { fn: 'updateBankTransaction', id, dept })
  upsertLocal('bankTransactions', dept, updated)
  return updated
}

export async function deleteBankTransaction(id, dept) {
  await restDeleteSafe(`bankTransactions/${id}`, { fn: 'deleteBankTransaction', id, dept })
  removeLocal('bankTransactions', dept, id)
  return true
}

// ─── bankCharges CRUD ─────────────────────────────────────────────

export function getBankCharges(dept, { accountNumber, yearMonth } = {}) {
  let list = lsLoad('bankCharges', dept)
  if (accountNumber) {
    const acct = String(accountNumber).slice(-4)
    list = list.filter(c => String(c.accountNumber).slice(-4) === acct)
  }
  if (yearMonth) list = list.filter(c => c.yearMonth === yearMonth)
  return list
}

export async function refreshBankCharges(dept) {
  try {
    const list = await restQuery('bankCharges', 'departmentId', dept)
    lsSave('bankCharges', dept, list)
    return list
  } catch (err) {
    console.warn('[bankStore] refreshBankCharges failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', fn: 'refreshBankCharges', dept } })
    return lsLoad('bankCharges', dept)
  }
}

/** Save a bank charge summary line (one row per chargeType per month).
 *  ID is deterministic so re-uploads overwrite cleanly. */
export async function saveBankCharge(charge) {
  const {
    departmentId, accountNumber, bankName,
    yearMonth, chargeType, amount, statementDate,
  } = charge
  if (!departmentId) throw new Error('departmentId is required')
  if (!accountNumber) throw new Error('accountNumber is required')
  if (!yearMonth) throw new Error('yearMonth is required')
  if (!chargeType) throw new Error('chargeType is required')
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    throw new Error('amount must be a finite number')
  }

  const id = bankChargeId(departmentId, accountNumber, yearMonth, chargeType)
  const acct = String(accountNumber).slice(-4)
  const record = {
    id,
    departmentId,
    accountNumber: acct,
    bankName: bankName || '',
    yearMonth,
    chargeType,
    amount,
    statementDate: statementDate || '',
    createdAt: new Date().toISOString(),
  }
  await restSetSafe(`bankCharges/${id}`, record, { fn: 'saveBankCharge', id, departmentId, chargeType })
  upsertLocal('bankCharges', departmentId, record)
  return record
}

export async function saveBankChargesBatch(charges) {
  let saved = 0
  let failed = 0
  for (const c of charges) {
    try { await saveBankCharge(c); saved++ }
    catch (err) {
      console.warn('[bankStore] saveBankChargesBatch row failed:', err?.message)
      failed++
    }
  }
  return { saved, failed }
}

export async function deleteBankCharge(id, dept) {
  await restDeleteSafe(`bankCharges/${id}`, { fn: 'deleteBankCharge', id, dept })
  removeLocal('bankCharges', dept, id)
  return true
}

// ─── expenseCategories CRUD ───────────────────────────────────────

export function getExpenseCategories(dept) {
  return lsLoad('expenseCategories', dept)
}

export async function refreshExpenseCategories(dept) {
  try {
    const list = await restQuery('expenseCategories', 'departmentId', dept)
    lsSave('expenseCategories', dept, list)
    return list
  } catch (err) {
    console.warn('[bankStore] refreshExpenseCategories failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'bankStore', fn: 'refreshExpenseCategories', dept } })
    return lsLoad('expenseCategories', dept)
  }
}

export async function saveExpenseCategory({ departmentId, name, type, colour, keywords }) {
  if (!departmentId) throw new Error('departmentId is required')
  if (!name) throw new Error('name is required')
  const id = expenseCategoryId(departmentId, name)
  const existing = getExpenseCategories(departmentId).find(c => c.id === id)
  const record = {
    id,
    departmentId,
    name,
    type: type || 'expense',
    colour: colour || '#9ca3af',
    keywords: Array.isArray(keywords) ? keywords : [],
    createdAt: existing?.createdAt || new Date().toISOString(),
  }
  await restSetSafe(`expenseCategories/${id}`, record, { fn: 'saveExpenseCategory', id, departmentId, name })
  upsertLocal('expenseCategories', departmentId, record)
  return record
}

export async function deleteExpenseCategory(id, dept) {
  await restDeleteSafe(`expenseCategories/${id}`, { fn: 'deleteExpenseCategory', id, dept })
  removeLocal('expenseCategories', dept, id)
  return true
}

/** Idempotent: writes any of DEFAULT_EXPENSE_CATEGORIES that don't
 *  yet exist for this department. Safe to call repeatedly. */
export async function seedDefaultExpenseCategories(dept) {
  if (!dept) throw new Error('dept is required')
  // Refresh first so we don't try to recreate categories that already
  // exist on the server but aren't in this device's cache yet.
  await refreshExpenseCategories(dept).catch(() => {})
  const existing = new Set(getExpenseCategories(dept).map(c => c.id))
  let created = 0
  for (const cat of DEFAULT_EXPENSE_CATEGORIES) {
    const id = expenseCategoryId(dept, cat.name)
    if (existing.has(id)) continue
    try {
      await saveExpenseCategory({ departmentId: dept, ...cat })
      created++
    } catch (err) {
      console.warn('[bankStore] seedDefaultExpenseCategories failed for', cat.name, err?.message)
    }
  }
  return { created, total: DEFAULT_EXPENSE_CATEGORIES.length }
}

// ─── Auto-categorisation helper ───────────────────────────────────

/** Best-effort category match: lowercases the description and
 *  returns the first category whose `keywords` list contains a
 *  substring of the description. Falls back to 'Other'. */
export function suggestCategory(description, categories) {
  const desc = String(description || '').toLowerCase()
  if (!desc) return 'Other'
  const list = Array.isArray(categories) && categories.length > 0
    ? categories
    : DEFAULT_EXPENSE_CATEGORIES.map(c => ({ name: c.name, keywords: c.keywords }))
  for (const cat of list) {
    const kws = Array.isArray(cat.keywords) ? cat.keywords : []
    for (const kw of kws) {
      if (kw && desc.includes(String(kw).toLowerCase())) return cat.name
    }
  }
  return 'Other'
}
